import { User } from 'services/interface';

export const USER_ROLES_ACCESS_DENIED = {
    'admin': [],
    'user': ['security', 'notifications', 'workflows', 'engines', 'secure-pipeline-studio'],
    'analyst': ['analyze', 'security', 'notifications', 'workflows', 'engines', 'secure-pipeline-studio'],
} as const;

export type UserRole = keyof typeof USER_ROLES_ACCESS_DENIED;

const getUserRole = (user: User | null | undefined): UserRole => {
    if (!user?.org_role) {
        return 'user';
    }

    const role = user.org_role;

    if (!(role in USER_ROLES_ACCESS_DENIED)) {
        return 'user';
    }

    return role;
};

export const canAccessPage = (user: User | null | undefined, page: string): boolean => {
    const role = getUserRole(user);
    const deniedPages = USER_ROLES_ACCESS_DENIED[role] as readonly string[];

    return !deniedPages.includes(page);
};

export const canAccessPath = (user: User | null | undefined, pathname: string): boolean => {
    if (!pathname) return true;

    const pathParts = pathname.split('/').filter(Boolean);

    if (pathParts.length === 0) return true; // Home page

    const [section, tab] = pathParts;

    // Dashboard tabs: check 'dashboard' access only (not individual tabs)
    if (section === 'dashboard') {
        return canAccessPage(user, 'dashboard');
    }

    // Settings/Resources tabs: check specific tab access
    if ((section === 'settings' || section === 'resources') && tab) {
        return canAccessPage(user, tab);
    }

    // Secure Pipeline Studio: check workflows and engines access
    if (section === 'secure-pipeline-studio') {
        // Check if user has access to secure-pipeline-studio
        if (!canAccessPage(user, 'secure-pipeline-studio')) {
            return false;
        }
        // If there's a sub-path (workflows or engines), check that too
        if (tab) {
            return canAccessPage(user, tab);
        }
        return canAccessPage(user, 'secure-pipeline-studio');
    }

    // Top-level pages: check page name
    if (pathParts.length === 1) {
        return canAccessPage(user, section);
    }

    return true;
};
